import React from 'react';
import { components } from '../mdx/MdxComponents';
import TableOfContents from '../mdx/TableOfContents';
import AuthorCard from '../mdx/AuthorCard';
import ShareButtons from '../mdx/ShareButtons';
import ReadingProgress from '../mdx/ReadingProgress';
import BlogSectionGrid from './BlogSectionGrid';
import { CalendarDays, Clock, Gauge } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BlogPost, getSortedPosts } from '@/services/blogService';

interface BlogPostViewProps {
  post: BlogPost;
}

const getDifficultyColor = (difficulty?: string) => {
  switch (difficulty) {
    case 'Beginner': return 'bg-green-500/10 text-green-500 border-green-500/20';
    case 'Intermediate': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    case 'Advanced': return 'bg-red-500/10 text-red-500 border-red-500/20';
    default: return 'bg-primary/10 text-primary border-primary/20';
  }
};

const BlogPostView: React.FC<BlogPostViewProps> = ({ post }) => {
  const { Content } = post;

  // Get 3 related articles (just excluding current for now, keeping it simple)
  const relatedPosts = getSortedPosts()
    .filter(p => p.id !== post.id)
    .slice(0, 3);

  return (
    <>
      <ReadingProgress />
      <article className="relative">
        {/* Premium Hero Section */}
        <div className="mb-12 border-b border-border/50 pb-12">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-none px-3 py-1 text-sm font-medium">
              {post.category}
            </Badge>
            {post.difficulty && (
              <Badge variant="outline" className={`px-3 py-1 text-sm font-medium border ${getDifficultyColor(post.difficulty)}`}>
                {post.difficulty}
              </Badge>
            )}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.15]">
            {post.title}
          </h1>

          <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl">
            {post.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-6 text-sm font-medium text-muted-foreground">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{post.readingTime}</span>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="flex flex-col lg:flex-row gap-12 relative">
          {/* Main Content Area */}
          <div className="lg:w-[70%] min-w-0">
            <div className="prose prose-invert prose-lg max-w-none prose-headings:scroll-mt-28 prose-a:text-primary">
              {Content ? (
                <Content components={components} />
              ) : (
                <p>Content could not be loaded.</p>
              )}
            </div>

            <div className="mt-16">
              <ShareButtons url={`https://aniketkasale.in/blog/${post.slug || post.id}`} title={post.title} />
              <AuthorCard />
            </div>
          </div>

          {/* Sidebar Area */}
          <div className="hidden lg:block lg:w-[30%]">
            <TableOfContents />
          </div>
        </div>
      </article>

      {/* Related Articles Section */}
      {relatedPosts.length > 0 && (
        <div className="mt-20 pt-16 border-t border-border/50">
          <h2 className="text-2xl font-bold mb-8">You may also enjoy</h2>
          <BlogSectionGrid posts={relatedPosts} isVisible={true} />
        </div>
      )}
    </>
  );
};

export default BlogPostView;
